/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/prefer-optional-chain */
// @flow
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { TypeMenu } from './TypeMenu';
import MenuItem from './MenuItem';
import styled from 'styled-components';
import { ThemeContext } from '../../styles/ThemeContext';
import ClickAwayListener from 'react-click-away-listener';
import { UserContext } from '../../contexts/UserContext';
import DhlText from '../DhlText';
import { useTranslation } from 'react-i18next';
import Item from './Item';
import { logout, validateDHLEmailDomain } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { PATHS, ROLES } from '../../pages/App/constants';
import { NOTIFICATION_TYPES, NotificationContext } from '../../contexts/NotificationContext';
import { fetchIcon } from '../../assets/Icons';
import publicServices from '../../service/publicServices';
import { ApplicationBrandingContext } from '../../contexts/ApplicationBrandingContext';

const ContextWrapper = styled.div`
  position: relative;
  display: inline-block;
  min-width: 100px;
  z-index: 4;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const PopupWrapper = styled.div`
  width: 100%;
  min-width: 200px;
  min-height: 100px;
  max-height: calc(100vh - 100px);
  background-color: ${(props) => props.theme.background};
  color: ${(props) => props.theme.primaryText};
  border-radius: 6px;
  padding: 8px;
  position: absolute;
  display: flex;
  flex-direction: column;
  z-index: 2;
  top: 10%;
  left: 40%;
  margin-left: -80px;
  overflow-y: auto;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
`;

const PopupDivider = styled.div`
  width: 100%;
  height: 1px;
  margin-top: 8px;
  background-color: ${(props) => props.theme.divider};
`;

const HeaderText = styled(DhlText)`
  width: 100%;
  margin-top: 8px;
  cursor: null;
`;

const AppSwitcherWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  gap: 0px;
  align-items: center;
  align-self: flex-start;
  background: ${(props) => props.theme.switcherHoverBg};
  border-radius: 12px;
  padding: 2px;
  box-shadow: -1px 2px 8px 0px rgba(0,0,0,0.23);
`;

const ActiveAppButton = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  background: ${(props) => props.theme.switcherActiveBg};
  border: 1px solid ${(props) => props.theme.switcherBorder};
  border-radius: 11px;  
  padding: 3px 6px 3px 3px;
  cursor: default;
  font-size: 10px;
  font-weight: 600;
  color: ${(props) => props.theme.secondaryText};
  outline: none;
`;

const InactiveAppButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 3px 3px;
  cursor: pointer;
  border-radius: 10px;
  color: ${(props) => props.theme.switcherInactiveColor};

  &:hover {
    background: ${(props) => props.theme.switcherHoverBg};
  }
`;

const ProfileHeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export const iconTypeEnum = {
  BASE64: 'base64',
  NONE: 'none',
};

const ProfileContext: React.FC<TypeMenu> = (props) => {
  const { isMenuOpen, toggleMenu, isBrandingObjectShowing, brandingTextColor, setImpersonatorModal } = props;
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const { appBrand } = useContext(ApplicationBrandingContext);
  const {setType, setMessage} = useContext(NotificationContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isDhlUser = user?.isDhl || validateDHLEmailDomain(user?.email ?? '');

  const isLifeTrack = window.location.origin.includes('lifetrack');
  const lifetrackLabel = 'LifeTrack';
  const mscLabel = 'MySC';

  const [otherAppBaseUrl, setOtherAppBaseUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isDhlUser) return;

    const origin = window.location.origin;
    let otherAppUrl: string;
    if (isLifeTrack) {
      const match = origin.match(/lifetrack([^.]*)/);
      // mysupplychain never carries the "beta" marker, even for onb/prod
      const suffix = (match?.[1] ?? '').replace('-beta', '');
      otherAppUrl = `https://mysupplychain${suffix}.dhl.com`;
    } else {
      const match = origin.match(/mysupplychain([^.]*)/);
      const suffix = match?.[1] ?? '';
      // exception: onb ('-onb') and production (no suffix) environments
      // point to the "beta" lifetrack environment
      const betaPrefix = suffix === '' || suffix === '-onb' ? '-beta' : '';
      otherAppUrl = `https://lifetrack${betaPrefix}${suffix}.dhl.com`;
    }
    publicServices.getApplicationBrandings(encodeURI(otherAppUrl))
      .then((response) => {
        if (response.data.length > 0) {
          setOtherAppBaseUrl(response.data[0].mysc_base_url);
        }
      })
      .catch(() => { /* silently ignore — switcher button simply won't render */ });
  }, [isDhlUser]);

  const lifetrackBaseUrl = isLifeTrack ? appBrand?.mysc_base_url : otherAppBaseUrl;
  const mscBaseUrl = isLifeTrack ? otherAppBaseUrl : appBrand?.mysc_base_url;

  const handleProductSwitch = useCallback((targetBaseUrl: string | null | undefined) => {
    if (targetBaseUrl == null) return;
    // Validate targetBaseUrl is a safe https origin
    if (!/^https:\/\/[a-zA-Z0-9._-]+(:\d+)?$/.test(targetBaseUrl)) return;

    // Sanitize each component: strip characters outside the safe URL charset.
    // This explicit allowlist breaks the Checkmarx taint chain from
    // window.location.search / .hash to the final href assignment.
    const safePath = window.location.pathname.replace(/[^\w/._~:@!$&'()*+,;=-]/g, '');
    const safeSearch = window.location.search.replace(/[^\w/._~:@!$&'()*+,;=?%-]/g, '');
    const safeHash = window.location.hash.replace(/[^\w/._~:@!$&'()*+,;=#%-]/g, '');

    const destination = new URL(safePath + safeSearch + safeHash, targetBaseUrl);
    // Marks the landing app that this navigation came from the in-app switcher
    destination.searchParams.set('mscAppSwitch', '1');

    // Final guard: confirm origin was not overridden
    if (destination.origin !== new URL(targetBaseUrl).origin) return;

    toggleMenu(false);
    window.location.href = destination.href;
  }, [toggleMenu]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (isMenuOpen) return toggleMenu(false);
      }}
    >
      <ContextWrapper theme={theme}>
        {
          <MenuItem
            key={`profile-item`}
            label={`${user.firstName}`}
            type="label"
            role={user.roles?.length > 0 ? user.roles[0] : ""}
            icon="fa-circle-user"
            onSelect={() => toggleMenu(true)}
            isProfile={true}
            isBrandingObjectShowing={isBrandingObjectShowing}
            brandingTextColor={brandingTextColor}
          />
        }

        {isMenuOpen && (
          <PopupWrapper theme={theme}>
            <ProfileHeaderRow>
              <MenuItem
                key={`profile-item`}
                label={`${user.firstName}`}
                onSelect={() => toggleMenu(false)}
                role={user.roles?.length > 0 ? user.roles[0] : ""}
                icon="fa-circle-user"
                isProfile={true}
              />

              {isDhlUser && (lifetrackBaseUrl != null || mscBaseUrl != null) && (
                <AppSwitcherWrapper theme={theme}>
                  {isLifeTrack ? (
                    <ActiveAppButton type="button" theme={theme}>
                      <img src={fetchIcon('Lifetrack', false) as string} alt={lifetrackLabel} style={{ width: 20, height: 20 }} />
                      {lifetrackLabel}
                    </ActiveAppButton>
                  ) : (
                    <InactiveAppButton
                      type="button"
                      theme={theme}
                      onClick={() => { handleProductSwitch(lifetrackBaseUrl); }}
                      title={lifetrackLabel}
                    >
                      <img src={fetchIcon('Lifetrack', false) as string} alt={lifetrackLabel} style={{ width: 20, height: 20 }} />
                    </InactiveAppButton>
                  )}
                  {!isLifeTrack ? (
                    <ActiveAppButton type="button" theme={theme}>
                      <img src={fetchIcon('Globe', false) as string} alt={mscLabel} style={{ width: 18, height: 18 }} />
                      {mscLabel}
                    </ActiveAppButton>
                  ) : (
                    <InactiveAppButton
                      type="button"
                      theme={theme}
                      onClick={() => { handleProductSwitch(mscBaseUrl); }}
                      title={mscLabel}
                    >
                      <img src={fetchIcon('Globe', false) as string} alt={mscLabel} style={{ width: 18, height: 18 }} />
                    </InactiveAppButton>
                  )}
                </AppSwitcherWrapper>
              )}
            </ProfileHeaderRow>

            <HeaderText
              size={'xs'}
              weight={600}
              title={t('App.page.Home.myOptions')}
              color="#b2b2b2"
            />

            <PopupDivider theme={theme} />

            <Item
              label={t('App.page.Home.profile')}
              onSelect={() => {
                if(user?.impersonated !== undefined && user?.impersonated){
                  setMessage(t('App.page.Impersonator.impersonation.notAllowedInImpersonationMode'));
                  setType(NOTIFICATION_TYPES.FAILED);
                } else {
                  toggleMenu(false);
                  navigate(PATHS.PROFILE);
                }
              }}
              icon="fa-user"
            />

            {user.adminMenuLinks != null && user.adminMenuLinks.length > 0 && (
              <>
                <HeaderText
                  size={'xs'}
                  weight={600}
                  title={t('App.page.Home.adminOptions')}
                  color="#b2b2b2"
                />

                <PopupDivider theme={theme} />
              </>
            )}

            {user.adminMenuLinks && user.adminMenuLinks.map((menuItem) => (
              <Item
                key={menuItem.name}
                label={menuItem.name}
                onSelect={() => {
                  if (menuItem.name === 'Create External User') {
                    toggleMenu(false);
                    navigate(PATHS.EXTERNAL_USER);
                  } else if (user?.impersonated && (menuItem.name === 'Users' || menuItem.name === 'Groups' || menuItem.name === 'Templates' || menuItem.name === 'Business Functions')) {
                    toggleMenu();
                    // raf apps are not allowed in impersonation mode
                    setType(NOTIFICATION_TYPES.FAILED);
                    setMessage(t('App.page.Impersonator.impersonation.notAllowedInImpersonationMode'))
                  } else if (menuItem.internal) {
                    toggleMenu(false);
                    navigate(menuItem.url);
                  } else {
                    toggleMenu(false);
                    window.open(
                      `/${process.env.REACT_APP_MSC_PATH}${menuItem.url}`,
                      '_self'
                    );
                  }
                }}
                icon={menuItem.icon}
              />
            ))}

            { ((user?.impersonator && validateDHLEmailDomain(user?.email)) ?? false) &&
              (
                <Item
                  label={t('App.page.Home.impersonator')}
                  key={'App.page.Home.impersonator.menu'}
                  // url={undefined}
                  icon={'fas fa-people-arrows'}
                  onSelect={() => {
                    toggleMenu(false);
                    if(setImpersonatorModal !== undefined){
                      setImpersonatorModal(true)
                    }
                  }}
                />
              )
            }

            {user.roles && user.roles.includes(ROLES.MSCADMIN) &&
              <Item
                key={'users-dscum'}
                label={"Users (DSCUM)"}
                onSelect={() => {
                  toggleMenu(false);
                  navigate(PATHS.USERS);
                }}
                icon="fa-user"
              />
            }

            <PopupDivider theme={theme} />

            <Item
              label={t('App.page.Home.logout')}
              onSelect={() => {
                toggleMenu(false);
                logout().catch(err => console.error('Logout error:', err));
              }}
              icon="right-from-bracket"
            />
          </PopupWrapper>
        )}
      </ContextWrapper>
    </ClickAwayListener>
  );
};

export default ProfileContext;
